import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { useFavorites } from '@/hooks/useFavorites';
import { useTourDiscounts, resolveTourDiscount, applyTourDiscount } from '@/hooks/useTourDiscounts';

interface SavedTour {
  id: string;
  title: string;
  duration: string;
  spots: number;
  category: string;
  group_type: string;
  price: number;
  image: string;
  rating_score: number | null;
  rating_count: number | null;
}

export default function SavedToursTab() {
  const { favoriteIds, toggleFavorite, loading: favoritesLoading } = useFavorites();
  const { t } = useTranslation();
  const { discounts } = useTourDiscounts();
  const [tours, setTours] = useState<SavedTour[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const ids = Array.from(favoriteIds);

    if (ids.length === 0) {
      setTours([]);
      setLoading(false);
      setError(null);
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    setError(null);

    supabase
      .from('vietnamtravel_tours')
      .select('id, title, duration, spots, category, group_type, price, image, rating_score, rating_count')
      .in('id', ids)
      .then(({ data, error: err }) => {
        if (cancelled) return;
        if (err) {
          setError(err.message);
          setTours([]);
        } else {
          setTours(data ?? []);
        }
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [favoriteIds]);

  if (favoritesLoading || loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[1, 2].map((i) => (
          <div key={i} className="bg-white border border-secondary-200 rounded-lg overflow-hidden animate-pulse">
            <div className="h-40 bg-secondary-100" />
            <div className="p-4 space-y-3">
              <div className="h-4 bg-secondary-100 rounded w-3/4" />
              <div className="h-3 bg-secondary-100 rounded w-1/2" />
              <div className="h-5 bg-secondary-100 rounded w-1/3" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 mx-auto rounded-full bg-red-50 flex items-center justify-center">
          <i className="ri-error-warning-line text-2xl text-red-400" />
        </div>
        <h3 className="mt-4 font-heading text-lg font-bold text-secondary-900">{t('profile.somethingWrong')}</h3>
        <p className="mt-1 text-sm text-secondary-500">{error}</p>
        <button
          type="button"
          onClick={() => setError(null)}
          className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-md px-4 py-2 transition-colors whitespace-nowrap"
        >
          <i className="ri-refresh-line text-sm" />
          {t('profile.tryAgain')}
        </button>
      </div>
    );
  }

  if (tours.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 mx-auto rounded-full bg-secondary-100 flex items-center justify-center">
          <i className="ri-heart-3-line text-2xl text-secondary-500" />
        </div>
        <h3 className="mt-4 font-heading text-lg font-bold text-secondary-900">{t('profile.noSaved')}</h3>
        <p className="mt-1 text-sm text-secondary-500">
          {t('profile.noSavedText')}
        </p>
        <Link
          to="/tours"
          className="mt-4 inline-flex items-center justify-center gap-1.5 text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-md px-4 py-2 transition-colors whitespace-nowrap"
        >
          {t('profile.exploreTours')}
          <i className="ri-arrow-right-line text-sm" />
        </Link>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {tours.map((tour) => {
        const priceInfo = applyTourDiscount(
          Number(tour.price),
          resolveTourDiscount(tour.id, discounts),
        );
        return (
          <div
            key={tour.id}
            className="bg-white border border-secondary-200 rounded-lg overflow-hidden group"
          >
            <div className="relative h-40">
              <img
                src={tour.image}
                alt={tour.title}
                title={`${tour.title} Vietnam tour`}
                className="w-full h-full object-cover object-top"
              />
              <button
                onClick={() => toggleFavorite(tour.id)}
                aria-label={t('profile.removeTour', { title: tour.title })}
                className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-white/90 text-red-500 hover:bg-white transition-colors cursor-pointer"
              >
                <i className="ri-heart-3-fill text-base" />
              </button>
              <span className="absolute bottom-3 left-3 text-xs font-semibold text-white bg-black/50 rounded-full px-3 py-1 whitespace-nowrap">
                {tour.duration}
              </span>
            </div>
            <div className="p-4">
              <h3 className="font-heading text-base font-bold text-secondary-900 leading-snug">
                {tour.title}
              </h3>
              <div className="mt-1 flex flex-wrap items-center gap-1.5 text-sm text-secondary-500">
                <span className="inline-flex items-center gap-1">
                  <i className="ri-map-pin-line text-base text-primary-600" />
                  {(tour.category || '').split(',')[0].trim()}
                </span>
                <span className="inline-flex items-center gap-1">
                  <i className="ri-flag-line text-base text-primary-600" />
                  {tour.group_type}
                </span>
              </div>
              <div className="mt-3 flex items-center justify-between">
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-secondary-900">
                  <i className="ri-star-fill text-sm text-accent-500" />
                  {tour.rating_score != null ? Number(tour.rating_score).toFixed(1) : '—'}
                  {tour.rating_count != null && (
                    <span className="font-normal text-secondary-400">({tour.rating_count})</span>
                  )}
                </span>
                <span className="font-heading text-lg font-bold text-primary-600 flex items-baseline gap-1.5">
                  {priceInfo.hasDiscount && (
                    <span className="text-sm font-medium text-secondary-400 line-through">
                      ${priceInfo.originalPrice.toLocaleString()}
                    </span>
                  )}
                  ${priceInfo.finalPrice.toLocaleString()}
                </span>
              </div>
              <Link
                to={`/tour/${tour.id}`}
                className="mt-3 w-full inline-flex items-center justify-center gap-1.5 text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-md px-4 py-2 transition-colors whitespace-nowrap"
              >
                {t('profile.viewTour')}
                <i className="ri-arrow-right-line text-sm" />
              </Link>
            </div>
          </div>
        );
      })}
    </div>
  );
}